import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  ArrowLeft, Plus, PoundSterling, Users, Eye, EyeOff, CheckCircle,
  XCircle, Loader2, Edit, Trash2, Phone, Mail, Calendar, RefreshCw
} from "lucide-react";
import { API } from "@/App";

export const CaseOffersPage = () => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [offers, setOffers] = useState([]);
  const [milestones, setMilestones] = useState({});
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [saving, setSaving] = useState(false);

  // New offer form
  const [offerForm, setOfferForm] = useState({
    buyer_name: "",
    buyer_email: "",
    buyer_phone: "",
    amount: "",
    conditions: "",
    is_cash_buyer: false,
    chain_status: "no_chain",
    mortgage_agreed: false,
    hide_amount: false
  });

  useEffect(() => {
    fetchData();
  }, [caseId]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [caseRes, offersRes, milestonesRes] = await Promise.all([
        axios.get(`${API}/sales/cases/${caseId}`, { headers }),
        axios.get(`${API}/portal/offers/case/${caseId}`, { headers }),
        axios.get(`${API}/portal/milestones/case/${caseId}`, { headers })
      ]);

      setCaseData(caseRes.data.case);
      setOffers(offersRes.data.offers || []);
      setMilestones(milestonesRes.data || {});
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOffer = async () => {
    if (!offerForm.buyer_name || !offerForm.amount) {
      toast.error("Please fill in buyer name and amount");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...offerForm,
        case_id: caseId,
        amount: parseFloat(offerForm.amount)
      };

      if (editingOffer) {
        await axios.put(`${API}/portal/offers/${editingOffer.offer_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Offer updated");
      } else {
        await axios.post(`${API}/portal/offers/create`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Offer created");
      }

      setShowOfferModal(false);
      resetOfferForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save offer");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOfferStatus = async (offerId, status) => {
    try {
      if (status === "accepted") {
        // Use the automation endpoint for accepting offers
        // This triggers: buyer account creation, magic link, milestones, deal workspace
        if (!window.confirm("Are you sure you want to accept this offer? This will:\n• Update property status to SSTC\n• Send login details to buyer\n• Initialize sale progression\n• Reject all other pending offers")) {
          return;
        }
        await axios.post(`${API}/sales-automation/offers/accept`, {
          offer_id: offerId,
          notes: "Accepted via Offers page"
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Offer accepted! Buyer has been notified with login details.");
      } else {
        // For other statuses, use the simple update endpoint
        await axios.put(`${API}/portal/offers/${offerId}`, { status }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`Offer ${status}`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update offer");
    }
  };

  const handleToggleHideAmount = async (offer) => {
    try {
      await axios.put(`${API}/portal/offers/${offer.offer_id}`, {
        hide_amount: !offer.hide_amount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(offer.hide_amount ? "Amount now visible to seller" : "Amount hidden from seller");
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!confirm("Are you sure you want to delete this offer?")) return;

    try {
      await axios.delete(`${API}/portal/offers/${offerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Offer deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete offer");
    }
  };

  const resetOfferForm = () => {
    setOfferForm({
      buyer_name: "",
      buyer_email: "",
      buyer_phone: "",
      amount: "",
      conditions: "",
      is_cash_buyer: false,
      chain_status: "no_chain",
      mortgage_agreed: false,
      hide_amount: false
    });
    setEditingOffer(null);
  };

  const openEditOffer = (offer) => {
    setOfferForm({
      buyer_name: offer.buyer_name || "",
      buyer_email: offer.buyer_email || "",
      buyer_phone: offer.buyer_phone || "",
      amount: offer.amount?.toString() || "",
      conditions: offer.conditions || "",
      is_cash_buyer: offer.is_cash_buyer || false,
      chain_status: offer.chain_status || "no_chain",
      mortgage_agreed: offer.mortgage_agreed || false,
      hide_amount: offer.hide_amount || false
    });
    setEditingOffer(offer);
    setShowOfferModal(true);
  };

  const formatCurrency = (amount) => `£${parseFloat(amount || 0).toLocaleString()}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(`/sales/case/${caseId}`)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold">Offers & Milestones</h1>
                <p className="text-sm text-muted-foreground">
                  {caseData?.address_line1}, {caseData?.postcode}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => { resetOfferForm(); setShowOfferModal(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Offer
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Property Summary */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Asking Price</p>
                <p className="text-2xl font-bold">{formatCurrency(caseData?.offer_price || caseData?.asking_price)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Offers</p>
                <p className="text-2xl font-bold">{offers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Offers List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PoundSterling className="w-5 h-5" />
              Offers ({offers.length})
            </CardTitle>
            <CardDescription>
              Manage offers for this property. Toggle visibility to control what sellers see.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {offers.length === 0 ? (
              <div className="text-center py-8">
                <PoundSterling className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">No offers yet</p>
                <Button className="mt-4" onClick={() => setShowOfferModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Offer
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {offers.map((offer) => (
                  <div 
                    key={offer.offer_id} 
                    className={`border rounded-lg p-4 ${
                      offer.status === "accepted" ? "border-green-300 bg-green-50" :
                      offer.status === "rejected" ? "border-red-200 bg-red-50" :
                      "border-slate-200"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="text-xl font-bold">{formatCurrency(offer.amount)}</p>
                          {offer.hide_amount && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              <EyeOff className="w-3 h-3 mr-1" />
                              Hidden from seller
                            </Badge>
                          )}
                          <Badge className={
                            offer.status === "accepted" ? "bg-green-100 text-green-700" :
                            offer.status === "rejected" ? "bg-red-100 text-red-700" :
                            offer.status === "withdrawn" ? "bg-slate-100 text-slate-700" :
                            "bg-amber-100 text-amber-700"
                          }>
                            {offer.status?.charAt(0).toUpperCase() + offer.status?.slice(1) || "Pending"}
                          </Badge>
                        </div>
                        <p className="font-medium mt-1">{offer.buyer_name}</p>
                        <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                          {offer.buyer_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {offer.buyer_email}
                            </span>
                          )}
                          {offer.buyer_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {offer.buyer_phone}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          {offer.is_cash_buyer && (
                            <Badge className="bg-blue-100 text-blue-700">Cash Buyer</Badge>
                          )}
                          {offer.mortgage_agreed && (
                            <Badge className="bg-purple-100 text-purple-700">Mortgage AIP</Badge>
                          )}
                          <Badge variant="outline">
                            {offer.chain_status === "no_chain" ? "No Chain" :
                             offer.chain_status === "first_time_buyer" ? "FTB" :
                             "In Chain"}
                          </Badge>
                        </div>
                        {offer.conditions && (
                          <p className="text-sm text-slate-600 mt-2 border-t pt-2">{offer.conditions}</p>
                        )}
                        {offer.agent_notes && (
                          <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded mt-2">
                            <strong>Agent Note:</strong> {offer.agent_notes}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleHideAmount(offer)}
                          title={offer.hide_amount ? "Make visible to seller" : "Hide from seller"}
                        >
                          {offer.hide_amount ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditOffer(offer)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => handleDeleteOffer(offer.offer_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {offer.status === "pending" && (
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => handleUpdateOfferStatus(offer.offer_id, "accepted")}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => handleUpdateOfferStatus(offer.offer_id, "rejected")}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-2">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      Received {new Date(offer.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add/Edit Offer Modal */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingOffer ? "Edit Offer" : "Add New Offer"}</DialogTitle>
            <DialogDescription>
              Enter the offer details. Toggle visibility to control what sellers can see.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Buyer Name *</Label>
                <Input
                  value={offerForm.buyer_name}
                  onChange={(e) => setOfferForm({ ...offerForm, buyer_name: e.target.value })}
                  placeholder="John Smith"
                  data-testid="offer-buyer-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Buyer Email</Label>
                <Input
                  type="email"
                  value={offerForm.buyer_email}
                  onChange={(e) => setOfferForm({ ...offerForm, buyer_email: e.target.value })}
                  placeholder="buyer@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Buyer Phone</Label>
                <Input
                  value={offerForm.buyer_phone}
                  onChange={(e) => setOfferForm({ ...offerForm, buyer_phone: e.target.value })}
                  placeholder="07xxx xxx xxx"
                />
              </div>
              <div className="space-y-2">
                <Label>Offer Amount (£) *</Label>
                <Input
                  type="number"
                  value={offerForm.amount}
                  onChange={(e) => setOfferForm({ ...offerForm, amount: e.target.value })}
                  placeholder="450000"
                  data-testid="offer-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Chain Status</Label>
                <Select
                  value={offerForm.chain_status}
                  onValueChange={(v) => setOfferForm({ ...offerForm, chain_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_chain">No Chain</SelectItem>
                    <SelectItem value="first_time_buyer">First Time Buyer</SelectItem>
                    <SelectItem value="chain">In Chain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Conditions / Notes</Label>
              <Textarea
                value={offerForm.conditions}
                onChange={(e) => setOfferForm({ ...offerForm, conditions: e.target.value })}
                placeholder="Subject to survey, etc."
                rows={2}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="is_cash_buyer"
                  checked={offerForm.is_cash_buyer}
                  onCheckedChange={(checked) => setOfferForm({ ...offerForm, is_cash_buyer: checked })}
                />
                <Label htmlFor="is_cash_buyer" className="cursor-pointer">Cash Buyer</Label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id="mortgage_agreed"
                  checked={offerForm.mortgage_agreed}
                  onCheckedChange={(checked) => setOfferForm({ ...offerForm, mortgage_agreed: checked })}
                />
                <Label htmlFor="mortgage_agreed" className="cursor-pointer">Mortgage AIP in Place</Label>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <Checkbox
                  id="hide_amount"
                  checked={offerForm.hide_amount}
                  onCheckedChange={(checked) => setOfferForm({ ...offerForm, hide_amount: checked })}
                />
                <div>
                  <Label htmlFor="hide_amount" className="cursor-pointer font-medium text-amber-800">
                    Hide amount from seller
                  </Label>
                  <p className="text-xs text-amber-600">Seller will see "Call to discuss" instead of the figure</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateOffer} disabled={saving} data-testid="save-offer-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingOffer ? "Update Offer" : "Add Offer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CaseOffersPage;
